import { Injectable, NgZone } from '@angular/core';

/**
 * Compat Ionic 7+ — animação do floating/stacked label no pattern LEGADO
 * `<ion-item><ion-label position="floating|stacked"><ion-input></ion-item>`.
 *
 * O Ionic 5 (produção) fazia o `<ion-item>` rastrear foco/valor do controle ANINHADO e adicionar
 * as classes `item-has-focus` / `item-has-value`; a CSS do `<ion-label>` responde a elas e o
 * rótulo "flutua" (sobe/encolhe ao focar, permanece em cima quando há valor, volta ao repouso
 * quando vazio e sem foco). No Ionic 7+ esse rastreamento foi REMOVIDO do pattern legado (a via
 * suportada passou a ser `<ion-input label labelPlacement>`), então o label não anima mais — fica
 * travado sobre o texto digitado (visível nos campos de busca de sabor/menu, pizza…).
 *
 * A CSS de animação do Ionic AINDA existe e continua respondendo às classes; este shim apenas as
 * re-aplica. Medido ao vivo (Ionic 8): adicionar `item-has-focus`/`item-has-value` leva o label de
 * `translateY(96%)` (repouso) para `translateY(50%) scale(0.75)` (flutuado) com a transição nativa.
 *
 * Registro ÚNICO de listeners delegados no `document` (fora da zona do Angular — foco/input
 * disparam muito e aqui só se mexe em `classList`, sem tocar estado Angular). Cobre a app inteira
 * sem exigir declaração por módulo. Ver memória `ionic8-ion-label-stacked-inline`.
 */
@Injectable({ providedIn: 'root' })
export class LegacyFloatingLabelService {
    /** Só age em `<ion-item>` que de fato tenha rótulo floating/stacked (os demais não animam). */
    private static readonly LABEL_SELECTOR =
        'ion-label[position="floating"], ion-label[position="stacked"]';
    private static readonly CONTROL_SELECTOR = 'ion-input, ion-textarea';

    private started = false;

    constructor(private zone: NgZone) {}

    /** Idempotente: registra os listeners uma vez. Chamado via APP_INITIALIZER. */
    init(): void {
        if (this.started || typeof document === 'undefined') {
            return;
        }
        this.started = true;

        this.zone.runOutsideAngular(() => {
            // `capture: true` para pegar focusin/focusout mesmo quando o evento não borbulha por
            // algum motivo; `input` borbulha e é composto (atravessa o shadow do ion-input).
            document.addEventListener('focusin', this.onFocusIn, true);
            document.addEventListener('focusout', this.onFocusOut, true);
            document.addEventListener('input', this.onInput, true);
        });
    }

    /** Remove os listeners. Não é necessário em runtime (shim vive o tempo do app); serve aos testes. */
    destroy(): void {
        if (!this.started) {
            return;
        }
        document.removeEventListener('focusin', this.onFocusIn, true);
        document.removeEventListener('focusout', this.onFocusOut, true);
        document.removeEventListener('input', this.onInput, true);
        this.started = false;
    }

    private readonly onFocusIn = (event: Event): void => {
        const item = this.relevantItem(event.target);
        if (item) {
            item.classList.add('item-has-focus');
        }
    };

    private readonly onFocusOut = (event: Event): void => {
        const item = this.relevantItem(event.target);
        if (item) {
            item.classList.remove('item-has-focus');
            this.markValue(item);
        }
    };

    private readonly onInput = (event: Event): void => {
        const item = this.relevantItem(event.target);
        if (item) {
            this.markValue(item);
        }
    };

    /** `<ion-item>` ancestral do alvo, mas só se ele tiver um rótulo floating/stacked. */
    private relevantItem(target: EventTarget | null): HTMLElement | null {
        const el = target as HTMLElement | null;
        const item = el && typeof el.closest === 'function'
            ? (el.closest('ion-item') as HTMLElement | null)
            : null;
        if (item && item.querySelector(LegacyFloatingLabelService.LABEL_SELECTOR)) {
            return item;
        }
        return null;
    }

    /** Liga/desliga `item-has-value` conforme o controle aninhado esteja preenchido. */
    private markValue(item: HTMLElement): void {
        const control = item.querySelector(
            LegacyFloatingLabelService.CONTROL_SELECTOR,
        ) as (Element & { value?: unknown }) | null;
        const value = control ? control.value : '';
        const hasValue = value != null && String(value).length > 0;
        item.classList.toggle('item-has-value', hasValue);
    }
}
